/**
 * Week 3: Theme Component Registry Loader
 * 
 * Runtime loader for theme component metadata.
 * Provides type-safe access to discovered components.
 */

import type { ComponentRegistry, ThemeSection, ThemeComponents } from "@/generated/theme-components";
import registryData from "@/generated/theme-components.json";

// Use static import instead of require() for browser compatibility
const registry: ComponentRegistry = registryData as ComponentRegistry;

/**
 * Loads the theme component registry (cached)
 */
export function loadComponentRegistry(): ComponentRegistry {
  return registry;
}

/**
 * Gets all available theme sections
 */
export function getThemeSections(): ThemeSection[] {
  const reg = loadComponentRegistry();
  return Object.keys(reg) as ThemeSection[];
}

/**
 * Gets all components in a section
 */
export function getSectionComponents<S extends ThemeSection>(
  section: S
): ThemeComponents[S][] {
  const reg = loadComponentRegistry();
  return Object.keys(reg[section] || {}) as ThemeComponents[S][];
}

/**
 * Gets default classes for a component
 */
export function getComponentDefaults<S extends ThemeSection>(
  section: S,
  component: ThemeComponents[S]
): string {
  const reg = loadComponentRegistry();
  return reg[section]?.[component]?.defaultClasses || "";
}

/**
 * Gets metadata for a component
 */
export function getComponentMetadata<S extends ThemeSection>(
  section: S,
  component: ThemeComponents[S]
) {
  const reg = loadComponentRegistry();
  return reg[section]?.[component] || null;
}

/**
 * Checks if a component exists in the registry
 */
export function hasComponent<S extends ThemeSection>(
  section: S,
  component: string
): component is ThemeComponents[S] {
  const reg = loadComponentRegistry();
  return !!(reg[section] && (component in reg[section]));
}

/**
 * Gets statistics about the registry
 */
export function getRegistryStats() {
  const reg = loadComponentRegistry();
  const sections = Object.keys(reg);
  const totalComponents = sections.reduce(
    (sum, section) => sum + Object.keys(reg[section]).length,
    0
  );

  return {
    sections: sections.length,
    components: totalComponents,
    sectionStats: sections.map((section) => ({
      section,
      components: Object.keys(reg[section]).length,
    })),
  };
}

/**
 * Development helper: Validate component usage
 * Warns if component is used but not in registry
 */
export function validateComponentUsage<S extends ThemeSection>(
  section: S,
  component: string
): boolean {
  if (!hasComponent(section, component)) {
    // console.warn(
    //   `[Theme] Component "${component}" in section "${section}" not found in registry. ` +
    //     `Did you forget to use a theme hook (useTheme/useSectionTheme/useThemeClasses)?`
    // );
    return false;
  }
  return true;
}

/**
 * Development helper: List all components using a specific hook type
 */
export function getComponentsByHookType(
  hookType: "useTheme" | "useSectionTheme" | "useThemeClasses"
) {
  const reg = loadComponentRegistry();
  const results: Array<{ section: string; component: string; file: string; line: number }> = [];

  for (const section of Object.keys(reg)) {
    for (const component of Object.keys(reg[section])) {
      const metadata = reg[section][component];
      if (metadata.hookType === hookType) {
        results.push({
          section,
          component,
          file: metadata.file,
          line: metadata.line,
        });
      }
    }
  }

  return results;
}

/**
 * Development helper: Find components with similar names (fuzzy search)
 */
export function findSimilarComponents(query: string, maxResults: number = 5) {
  const reg = loadComponentRegistry();
  const results: Array<{
    section: string;
    component: string;
    score: number;
  }> = [];

  const queryLower = query.toLowerCase();

  for (const section of Object.keys(reg)) {
    for (const component of Object.keys(reg[section])) {
      const componentLower = component.toLowerCase();

      // Simple similarity scoring
      let score = 0;
      if (componentLower === queryLower) score = 100;
      else if (componentLower.includes(queryLower)) score = 50;
      else if (queryLower.includes(componentLower)) score = 40;
      else {
        // Count matching characters
        const matches = queryLower.split("").filter((c) => componentLower.includes(c)).length;
        score = (matches / queryLower.length) * 30;
      }

      if (score > 0) {
        results.push({ section, component, score });
      }
    }
  }

  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults);
}
